/************************************************
 * Storage Key Constants
 ************************************************/
export const AI_BUTTON_HAS_LOADED_BEFORE_KEY = 'ai_button_has_loaded_before';
export const POLLING_SERVICE_KEY = 'polling_service';
export const USER_CONFIRMED_LOCATION_KEY = 'user_confirmed_location';
export const GRANULAR_METADATA_GENERATING = 'granular_metadata_generating';

/************************************************
 * Animation Constants
 ************************************************/
export const BUTTON_SHOW_ANIMATION_DELAY_MS = 3000; //3 seconds
export const BUTTON_SHOW_ANIMATION_DELAY_S = BUTTON_SHOW_ANIMATION_DELAY_MS / 1000;
export const BUTTON_SHOW_ANIMATION_DURATION_MS = 300;
export const BUTTON_SHOW_ANIMATION_DURATION_S = BUTTON_SHOW_ANIMATION_DURATION_MS / 1000;
export const MENU_SHOW_ANIMATION_DURATION_MS = 60;
export const MENU_SHOW_ANIMATION_DURATION_S = MENU_SHOW_ANIMATION_DURATION_MS / 1000;
export const MENU_BUTTON_ANIMATION_DELAY_MS = 4;
export const MENU_BUTTON_ANIMATION_DELAY_S = MENU_BUTTON_ANIMATION_DELAY_MS / 1000;
export const TRANSLATIONS_MAX_SPEAKERS_COUNT = 9;

/************************************************
 * Layout Constants
 ************************************************/
export const LOADER_BAR_HEIGHT = 48; // px
export const AVAILABLE_WINDOW_HEIGHT = 'calc(100vh - 146px)';

/************************************************
 * Z-Index Layers
 * Centralized z-index values to manage stacking order.
 * Use multiples of 10 to allow for easy insertion of new layers.
 ************************************************/
export const Z_INDEX = {
  // Base z-index for Bokeh Modal (external dependency)
  // Bokeh modals use 1400 by default
  BOKEH_MODAL: 1400,

  // Bokeh dropdown menus that need to appear above modals
  BOKEH_DROPDOWN_MENU: 1500,
} as const;

/************************************************
 * Timing Constants
 ************************************************/
export const SERVICE_CALL_DEBOUNCE_INTERVAL = 1000; //ms
export const MENU_OPEN_CLOSE_TIMING = 500; //ms
export const POLLING_INTERVAL = 5000;
export const POLLING_INTERVAL_SHORT = 2000;
export const POLLING_START_DELAY = 800;

/************************************************
 * Translations Constants
 ************************************************/
export const TRANSLATIONS_DISABLE_REASON_INCOMPLETE_TRANSCODING = 'transcoding_incomplete';

/************************************************
 * State Constants
 ************************************************/
export const FEATURE_DISABLED = 'feature_disabled';
export const ACTIVATION_PROCESSING = 'activation_processing';
export const TRANSCRIPTION_IN_PROGRESS = 'transcript_status_in_progress';

/************************************************
 * Enums
 ************************************************/

export const TRANSLATION_TRACK_ACTION_TYPES = {
  UP: 'up',
  DOWN: 'down',
  DELETE: 'delete',
  NONE: null,
};

export const SERVICE_STATUSES = {
  PROCESSING: 'PRCS',
  DONE: 'DONE',
  NONE_EXISTING: 'NONE_EXISTING',
  ERROR: 'ERROR',
  FAILED: 'FAILED',
  INITIALIZING: 'INIT',
  INVALID: 'INVALID',
};

/************************************************
 * Design Constants
 ************************************************/
export const LOADER_GRADIENT = `
    radial-gradient(40.7% 42.4% at 54.6% 52.4%, #19d5ff 0%, #19d5ff 36.78%, rgba(157, 0, 255, 0.28) 81.98%, rgba(157, 0, 255, 0) 100%);
`;

export const SHIMMER_GRADIENT = `
  radial-gradient(#82FFEB 0%, #17D5FF 30%, #0BA1FF 45%, #9E00FF 75%, #9E00FF 0%);
`;

export const RADIAL_BUTTON_GRADIENT_LIGHT = `
    radial-gradient(
      40.7% 42.4% at 54.6% 52.4%,
      #17d5ff 0%,
      #19d5ff 0%,
      #20d3ff 35.1%,
      rgba(157, 0, 255, 0.48) 72%,
      rgba(157, 0, 255, 0) 100%
    );
`;
export const RADIAL_BUTTON_GRADIENT_DARK = `
radial-gradient(
    40.7% 42.4% at 54.6% 52.4%,
    #17d5ff 0%,
    #20d3ff 35.1%,
    rgba(157, 0, 255, 0.48) 72%,
    rgba(157, 0, 255, 0) 100%);
`;

export const SCROLLBAR_STYLE_SX = {
  // Custom scrollbar styles
  '::-webkit-scrollbar-track': {
    background: 'transparent',
    border: 'none',
    padding: '2px',
  },
  '::-webkit-scrollbar': {
    width: '12px',
  },
  '::-webkit-scrollbar-thumb': {
    width: '3px',
    background: 'var(--chakra-colors-stroke)', // Use CSS variable for theme colors
    border: '3px solid transparent',
    borderRadius: '6px',
    backgroundClip: 'padding-box',
    _hover: {
      background: 'var(--chakra-colors-text-600)', // Adjust based on your theme
    },
  },
};

export const VIEWER_QNA_AI_THINKING_STYLE = `
  background: radial-gradient(rgb(23, 213, 255, 0.5) 10%, rgb(61, 127, 255, 0.5), rgb(173, 0, 255, 0.3), transparent 60%);
  background-repeat: no-repeat;
  background-size: 230% 230%;

  animation: gradient 5s linear infinite;
  @keyframes gradient {
    0%{background-position:0% 0%}
    50%{background-position:-10% -10%}
    100%{background-position:0% 0%}
  }
`;

export const VIEWER_QNA_ANSWER_PLACEHOLDER_ANIMATION = `
  animation: loading 3s linear infinite;
  @keyframes loading {
    0% {background-position: 200% 0}
    50% {background-position: 100% 0}
    100% {background-position: 0 0}
  }
`;

export const AI_PRIMARY_BUTTON_COLORS = {
  dark: {
    normal: '#FFFFFF !important',
    hover: '#E0E0E0 !important',
    disabled: '#585858 !important',
    textColor: '#000000 !important',
  },
  light: {
    normal: '#000000 !important',
    hover: '#282828 !important',
    disabled: '#808080 !important',
    textColor: '#FFFFFF !important',
  },
};

export const TO_MANY_QUESTIONS_ASKED_ERROR_CODE = 10017;
export const ASK_AI_DISABLED_ERROR_CODE = 10020;
export const CLIP_INTERACTIONS_LIMIT_ERROR_CODE = 10021;

export const QNA_LIMIT_ERRORS = [
  TO_MANY_QUESTIONS_ASKED_ERROR_CODE,
  ASK_AI_DISABLED_ERROR_CODE,
  CLIP_INTERACTIONS_LIMIT_ERROR_CODE,
];

export const VIDEO_TRANSLATIONS_STEPS = {
  TYPE_SELECTION: 'type_selection',
  SUBTITLING: 'subtitling',
  DUBBING: 'dubbing',
};

export const TranslationTypes = {
  DUBBING: 'dubbing',
  SUBTITLING: 'subtitling',
} as const;

export type TranslationType = typeof TranslationTypes[keyof typeof TranslationTypes];

export const PENDO_UPDATE_PAYMENT_METHODS_MODAL_GUIDE_ID = '10Fh7-3sYselCXGGIGdf1qa_fgs';
export const PENDO_EDITABLE_DUBBING_GUID_ID = 'HBhCWeBpico0-vnRiEdAx_g9GU8';
